/**
 * Hub アカウント連携（/auth/link + recoverAccount）。
 *
 * `auth.ts` が取得した ID Token を Hub にポストし、
 *   - 新規 → ゲスト YMG アカウントに provider を関連付け
 *   - 既存 → 409 ALREADY_LINKED + recoverable=true が返れば recoverAccount でアカウント復旧
 * を行う。
 *
 * 設計方針（参考: luckyanimal-sweeper/src/services/accountLink.ts）:
 *   - /auth/link には provider と token のみ送る（platformId 同時送信禁止 — knowladge.md）
 *   - 409 ALREADY_LINKED + recoverable=true のときは recoverAccount() にフォールバック
 *   - SDK の recoverAccount() が利用可能ならそれを使い、無ければ /auth/recover に直接 POST
 *
 * T12 時点では「呼び出し口だけ用意する」段階。
 * 実際にマルチプレイ側で session を使い始めるのは T13-T15。
 */

import { getYmgSdk, getGameId, ymgFetch, resetYmgSdkSession } from './sdk'
import {
  ACCOUNT_LINK_ENDPOINTS,
  AUTH_PREF_KEYS,
  AUTH_RECOVERY,
  type AuthProvider,
} from '../config/auth'
import { isCapacitorNative } from '../utils/platform'
import { initializeSocialLoginProviders } from './auth'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type LinkProvider = Exclude<AuthProvider, 'line'>

export interface LinkSuccess {
  ok: true
  recovered?: boolean
  /** 連携後に発行される session token。Hub 側で bootstrap 済みの場合は省略される。 */
  sessionToken?: string
  /** session token の更新に使う refresh token。Hub が返す場合のみ保存する。 */
  refreshToken?: string
  /** session token の失効時刻（ms epoch）。 */
  expiresAt?: number
  /** refresh token の失効時刻（ms epoch）。 */
  refreshExpiresAt?: number
  /** Hub session ID。 */
  sessionId?: string | null
}

export interface LinkFailure {
  ok: false
  code?: string
  message?: string
  /** ユーザーの明示的同意が必要な 409 ALREADY_LINKED ケース（autoRecover=false のとき）。 */
  needsRecoveryConfirm?: boolean
}

export type LinkResult = LinkSuccess | LinkFailure

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function prefGet(key: string): Promise<string | null> {
  if (!isCapacitorNative()) return null
  const { Preferences } = await import('@capacitor/preferences')
  const { value } = await Preferences.get({ key })
  return value ?? null
}

async function prefSet(key: string, value: string): Promise<void> {
  if (!isCapacitorNative()) return
  const { Preferences } = await import('@capacitor/preferences')
  await Preferences.set({ key, value })
}

async function persistRecoveredSession(params: {
  sessionToken?: string
  refreshToken?: string
  expiresAt?: number
  refreshExpiresAt?: number
  sessionId?: string | null
}): Promise<void> {
  // Web/LIFF 環境では何もしない。理由:
  //   - Web/LIFF では tokenProvider が毎回 LIFF ID Token または Firebase
  //     ID Token を返す設計のため、SDK 内部の session token に書き込む必要がない。
  //   - Apple/Google の連携 UI は availableProviders で Capacitor のみに
  //     限定されており、Web から recoverAccount が呼ばれること自体が無い。
  //   - SDK の useSessionToken/sessionStorage は Capacitor のみ有効化しており、
  //     Web で prefSet を呼んでも SDK は Preferences を読まない。
  //
  // Capacitor では SDK の sessionStorage adapter（同一の AUTH_PREF_KEYS.session
  // キーを使用）が裏で session を更新するため、ここでの prefSet は二重保証。
  if (!isCapacitorNative()) return

  const writes: Array<Promise<void>> = []
  if (params.sessionToken) {
    writes.push(prefSet(AUTH_PREF_KEYS.session, params.sessionToken))
  }
  if (params.refreshToken) {
    writes.push(prefSet(AUTH_PREF_KEYS.refresh, params.refreshToken))
  }
  if (typeof params.expiresAt === 'number') {
    writes.push(prefSet(AUTH_PREF_KEYS.sessionExpiresAt, String(params.expiresAt)))
  }
  if (typeof params.refreshExpiresAt === 'number') {
    writes.push(prefSet(AUTH_PREF_KEYS.refreshExpiresAt, String(params.refreshExpiresAt)))
  }
  if (params.sessionId) {
    writes.push(prefSet(AUTH_PREF_KEYS.sessionId, params.sessionId))
  }
  await Promise.all(writes)
}

// ---------------------------------------------------------------------------
// recoverAccount (existing account)
// ---------------------------------------------------------------------------

/**
 * 既存アカウントの復旧を行う。
 *
 * CDN SDK v1.1.0 は `sdk.recoverAccount()` 互換メソッドがあるが、
 * メソッド未定義の古いランタイムでは `/auth/recover` に直接 POST する。
 */
export async function recoverAccount(
  provider: LinkProvider,
  idToken: string,
): Promise<LinkResult> {
  try {
    const sdk = await getYmgSdk()
    const sdkWithRecover = sdk as unknown as {
      recoverAccount?: (p: { provider: LinkProvider; token: string }) => Promise<{
        sessionToken?: string
        refreshToken?: string
        expiresAt?: number
        refreshExpiresAt?: number
        sessionId?: string | null
      }>
    }

    if (typeof sdkWithRecover.recoverAccount === 'function') {
      const res = await sdkWithRecover.recoverAccount({ provider, token: idToken })
      await persistRecoveredSession(res ?? {})
      return { ok: true, recovered: true, sessionToken: res?.sessionToken }
    }
  } catch (err) {
    const errObj = err as { status?: number; code?: string; message?: string }
    if (errObj.status === 404 || errObj.code === 'ACCOUNT_NOT_FOUND') {
      return { ok: false, code: 'ACCOUNT_NOT_FOUND', message: 'アカウントが見つかりません' }
    }
    console.warn('[accountLink] SDK recoverAccount failed, trying direct endpoint:', err)
  }

  try {
    // Fallback: 直接 /auth/recover を叩く。
    // 期限切れ session が残っている状態の再認証でも使えるよう、Authorization は付けない。
    const res = await ymgFetch<{
      ok: boolean
      sessionToken?: string
      refreshToken?: string
      expiresAt?: number
      refreshExpiresAt?: number
      sessionId?: string
      error?: { code?: string; message?: string }
    }>(
      '/auth/recover',
      {
        method: 'POST',
        skipAuth: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider, token: idToken, gameId: getGameId() }),
      },
    )

    if (!res?.ok) {
      return {
        ok: false,
        code: res?.error?.code,
        message: res?.error?.message ?? 'recoverAccount failed',
      }
    }
    await persistRecoveredSession(res)
    await resetYmgSdkSession()
    return { ok: true, recovered: true, sessionToken: res.sessionToken }
  } catch (err) {
    const errObj = err as { status?: number; code?: string; message?: string }
    if (errObj.status === 404 || errObj.code === 'ACCOUNT_NOT_FOUND') {
      return { ok: false, code: 'ACCOUNT_NOT_FOUND', message: 'アカウントが見つかりません' }
    }
    return { ok: false, message: errObj.message ?? 'recoverAccount error' }
  }
}

// ---------------------------------------------------------------------------
// linkAccount (guest → registered)
// ---------------------------------------------------------------------------

/**
 * 現在の YMG ゲストアカウントに `provider` を関連付ける。
 *
 * 409 ALREADY_LINKED + recoverable=true が返った場合、
 * AUTH_RECOVERY.autoRecover に応じて自動的に recoverAccount() を呼ぶ。
 */
export async function linkAccount(
  provider: LinkProvider,
  idToken: string,
): Promise<LinkResult> {
  // Hub のセッション token は Capacitor のみ Preferences に保存される。
  // Web（ゲスト or LIFF）では SDK が内部で管理しており、
  // クライアント側からは直接参照しない。ここでは native only で session header を
  // 付けるフローを提供する（Web は SDK に任せる）。
  const sessionToken = await prefGet(AUTH_PREF_KEYS.session)

  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (sessionToken) headers['Authorization'] = `Bearer ${sessionToken}`

  // 注意: platformId を送らない（knowladge.md 既知制約）。
  const body = {
    provider,
    token: idToken,
    gameId: getGameId(),
  }

  try {
    const res = await ymgFetch<{
      ok: boolean
      sessionToken?: string
      refreshToken?: string
      expiresAt?: number
      refreshExpiresAt?: number
      sessionId?: string | null
      error?: { code?: string; message?: string; details?: { recoverable?: boolean } }
    }>(ACCOUNT_LINK_ENDPOINTS.link, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    })

    if (!res?.ok) {
      return {
        ok: false,
        code: res?.error?.code,
        message: res?.error?.message ?? '/auth/link failed',
      }
    }

    await persistRecoveredSession(res)
    await resetYmgSdkSession()
    return {
      ok: true,
      sessionToken: res.sessionToken,
      refreshToken: res.refreshToken,
      expiresAt: res.expiresAt,
      refreshExpiresAt: res.refreshExpiresAt,
      sessionId: res.sessionId,
    }
  } catch (err) {
    const errObj = err as {
      status?: number
      body?: { error?: { code?: string; message?: string; details?: { recoverable?: boolean } } }
      message?: string
    }

    // 409 ALREADY_LINKED with recoverable=true
    if (errObj.status === 409 && errObj.body?.error?.details?.recoverable === true) {
      if (AUTH_RECOVERY.autoRecover) {
        return await recoverAccount(provider, idToken)
      }
      return { ok: false, needsRecoveryConfirm: true, code: errObj.body.error.code }
    }

    return {
      ok: false,
      code: errObj.body?.error?.code,
      message: errObj.body?.error?.message ?? errObj.message ?? '/auth/link error',
    }
  }
}

// ---------------------------------------------------------------------------
// Linked providers query (GET /auth/providers)
// ---------------------------------------------------------------------------

interface ProviderEntry {
  provider: string
}

/**
 * 現在 Hub 側で連携済みのプロバイダ一覧を返す。未連携 / 未認証 / セッション切れの
 * 場合は空配列を返す（UI から呼ぶ用途のため、エラーで UI が崩れないように
 * 「サーバー応答が得られなかった = 連携情報なし」と同じ扱いにする）。
 *
 * `ymgFetch` は 401 を ApiError で throw するため、ここでは catch して空配列を返す。
 * ただしネットワーク障害（NetworkError）は呼び出し側に伝播させる方が望ましいケースも
 * あるため、再 throw するかは現時点では「ログのみ＋空配列」運用とする。
 * （フォールバックは fallback:ok として明示。Apple/Google 連携状態が見えないだけで
 *  ユーザー体験は致命的に壊れないため。）
 */
export async function getLinkedProviders(): Promise<LinkProvider[]> {
  try {
    const res = await ymgFetch<{ providers?: ProviderEntry[] }>(
      ACCOUNT_LINK_ENDPOINTS.providers,
      { method: 'GET' },
    )
    const list = res?.providers ?? []
    const known: LinkProvider[] = []
    for (const entry of list) {
      if (entry.provider === 'google' || entry.provider === 'apple') {
        known.push(entry.provider)
      }
    }
    return known
  } catch (err) {
    // fallback:ok — 連携状態の取得失敗で UI を完全停止させない（ボタンは「連携する」
    // 状態のまま表示）。エラー詳細はログでのみ追跡可能。
    const msg = err instanceof Error ? err.message : 'unknown'
    console.warn(`[accountLink] getLinkedProviders failed: ${msg}`)
    return []
  }
}

// ---------------------------------------------------------------------------
// Unlink (registered → guest)
// ---------------------------------------------------------------------------

/**
 * `provider` の連携を Hub から解除する低レベル API。
 *
 * 本人性検証のため、プロバイダの最新 ID Token を再取得して送信する
 * （balloon-float の同等実装と同じ設計）。`force: true` は同一 Hub アカウントを保ったまま
 * プロバイダ紐付けだけを外すモード。これを付けないと連携解除拒否されるケースがあるため
 * 明示する。
 */
async function unlinkAccount(
  provider: LinkProvider,
  idToken: string,
): Promise<LinkResult> {
  const sessionToken = await prefGet(AUTH_PREF_KEYS.session)
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (sessionToken) headers['Authorization'] = `Bearer ${sessionToken}`

  try {
    const res = await ymgFetch<{
      ok: boolean
      error?: { code?: string; message?: string }
    }>(ACCOUNT_LINK_ENDPOINTS.unlink, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        provider,
        token: idToken,
        gameId: getGameId(),
        force: true,
      }),
    })
    if (!res?.ok) {
      return {
        ok: false,
        code: res?.error?.code,
        message: res?.error?.message ?? '/auth/unlink failed',
      }
    }
    return { ok: true }
  } catch (err) {
    const errObj = err as {
      status?: number
      body?: { error?: { code?: string; message?: string } }
      message?: string
    }
    return {
      ok: false,
      code: errObj.body?.error?.code,
      message: errObj.body?.error?.message ?? errObj.message ?? '/auth/unlink error',
    }
  }
}

/**
 * Apple アカウントとの連携を解除する。Capacitor Native (iOS) 限定。
 * 内部で SocialLogin で Apple 再認証 → /auth/unlink。
 */
export async function unlinkAppleAccount(): Promise<LinkResult> {
  if (!isCapacitorNative()) {
    return { ok: false, message: 'Unlink is only available on the native build' }
  }
  try {
    await initializeSocialLoginProviders()
    const { SocialLogin } = await import('@capgo/capacitor-social-login')
    const result = await SocialLogin.login({ provider: 'apple', options: {} })
    const loginResult = (result as { result?: { idToken?: string } } | undefined)?.result
    if (!loginResult) {
      return { ok: false, message: 'Apple Sign-In cancelled' }
    }
    const idToken = loginResult.idToken
    if (!idToken) {
      return { ok: false, message: 'No Apple ID token returned' }
    }
    return unlinkAccount('apple', idToken)
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Unknown error'
    console.warn('[accountLink] unlinkApple failed:', err)
    return { ok: false, message: msg }
  }
}

/**
 * Google アカウントとの連携を解除する。Capacitor Native (iOS / Android) 限定。
 * 内部で SocialLogin で Google 再認証 → /auth/unlink。
 */
export async function unlinkGoogleAccount(): Promise<LinkResult> {
  if (!isCapacitorNative()) {
    return { ok: false, message: 'Unlink is only available on the native build' }
  }
  try {
    await initializeSocialLoginProviders()
    const { SocialLogin } = await import('@capgo/capacitor-social-login')
    const result = await SocialLogin.login({ provider: 'google', options: {} })
    const loginResult = (result as { result?: { responseType?: string; idToken?: string } } | undefined)?.result
    if (!loginResult || (loginResult.responseType && loginResult.responseType !== 'online')) {
      return { ok: false, message: 'Google Sign-In cancelled or offline response' }
    }
    const idToken = loginResult.idToken
    if (!idToken) {
      return { ok: false, message: 'No Google ID token returned' }
    }
    return unlinkAccount('google', idToken)
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Unknown error'
    console.warn('[accountLink] unlinkGoogle failed:', err)
    return { ok: false, message: msg }
  }
}
